import { useL10n } from "@ews/react-localization-context";
import
{
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonItemDivider,
    IonLabel,
    IonList,
    IonTitle,
    IonToolbar,
    useIonRouter
} from "@ionic/react";

import
{
    CSSProperties,
    FC, PropsWithChildren, ReactElement
} from "react";

import type { MenuToggleProps } from "../types";

import Icon from "@ews/react-icons";
import { Locale } from "../../../setup/locale";
import { ListProps } from "../../List/types";
import { ReactClickEvent } from "../../types";
import style from './Sidebar.module.scss';

export type MenuLinkProps = {
    title: Locale;
    link?: string;
    icon?: string | ReactElement;
    onClick?: (MenuToggleProps["onMenuToggle"]),
    id?: string;

};

export const MenuLink: FC<MenuLinkProps> = ({
    title,
    link = "",
    icon,
    onClick,
    id
}) =>
{
    const { translate: t } = useL10n();
    const router = useIonRouter();

    const onClickWrapper = (e: ReactClickEvent) =>
    {
        onClick && onClick(e);
        if (link) setTimeout(() => router.push(link));
    };

    return (
        <IonItem id={id} detail={false} onClick={onClickWrapper} button={true} >
            <IonLabel>{t(title)}</IonLabel>
            {icon && (typeof icon === 'string' ? <IonIcon icon={icon} /> : icon)}
        </IonItem>
    );

};

type MenuItemPops = PropsWithChildren & {
    className?: string;
    lines?: HTMLIonItemElement["lines"];
};

export const MenuItem: FC<MenuItemPops> = ({
    className,
    children,
    lines
}) =>
{
    return (
        <IonItem lines={lines} detail={false} button={false} className={className}>
            {children}
        </IonItem>
    );
};

type MenuSpacerProps = {
    label?: string;
};

export const MenuSpacer: FC<MenuSpacerProps> = ({
    label
}) =>
{
    const className = label ? "" : style.spacerFlat;
    return <IonItemDivider className={className}>{label && <IonLabel>{label}</IonLabel>}</IonItemDivider>;
};

type SidebarSectionProps = PropsWithChildren<{
    grow?: boolean,
    halfHeight?: boolean;
}>;

export const SidebarSection: FC<SidebarSectionProps> = ({
    children,
    grow = false,
    halfHeight = false
}) =>
{
    let style: CSSProperties = { maxHeight: "70vh", overflow: "auto" };

    if (grow) {
        style.flexGrow = 1;
    }

    if (halfHeight) {
        style = { ...style, overflow: "auto", maxHeight: "57vh" };
    }

    return (
        <IonList lines="none" style={style}>
            {children}
        </IonList >
    );
};


export const resizeMenuPopoverContent: ListProps["onPopupResize"] = ({ maxHeight, element }) =>
{
    const { children } = element;
    if (!children.length && !maxHeight) return;
    const popup = children[0] as HTMLIonPopoverElement;

    if (popup.clientHeight < maxHeight) return;

    let height = maxHeight;

    ;[...popup.children].forEach((child) =>
    {
        const _element = child as HTMLElement;
        const { clientHeight } = _element;

        if (child.nodeName.toLowerCase() === "ion-content") {
            _element.style.height = `${height}px`;
        } else {
            height -= clientHeight;
        }
    });
};



type SidebarProps = MenuToggleProps & PropsWithChildren<{
    title?: string;
}>;

export const Sidebar: FC<SidebarProps> = ({
    title,
    children,
    onMenuToggle = () => { },

}) =>
{
    return (
        <>
            {title ? <IonHeader>
                <IonToolbar>
                    <IonTitle>{title}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton size="large" onClick={onMenuToggle}>
                            <Icon icon={`close`} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader> : null}
            <IonContent>
                <div
                    className={`${style.content}`}
                >
                    {children}
                </div>
            </IonContent>
        </>
    );
};
